import React, { useMemo } from 'react';
import { Atividade, StatusAtividade } from '../../models/atividade';
import { AbaAtividadeDrawer, useMaestroContext } from '../../context/MaestroContext';
import { isMotorConciliacao, MotorConciliacaoDados } from '../../models/produtos';
import { listarDiasUteisNoIntervalo } from '../../utils/businessDays';
import { formatarData } from '../../utils/formatters';
import { COR_STATUS_ATIVIDADE, LABEL_STATUS_ATIVIDADE } from '../../utils/labels';
import Card from '../Card';
import EstadoVazio from '../EstadoVazio';

interface VisaoGanttProps {
  atividades: Atividade[];
  dependencias: Array<{ id: number; atividadeOrigemId: number; atividadeDestinoId: number }>;
  onSelecionar: (id: number, aba?: AbaAtividadeDrawer) => void;
}

const VisaoGantt: React.FC<VisaoGanttProps> = ({ atividades, dependencias, onSelecionar }) => {
  const { cicloAtivo, feriados, produtosIntegrados } = useMaestroContext();
  // Atividades sem dataPlanejada/dataPrevista (ex.: geradas manualmente fora do fluxo padrão de
  // template) não entram no Gantt — sem as duas datas não há como posicionar a barra sem fantasiar
  // um período; indiceColuna('') cairia sempre na coluna do dia 1, sugerindo um prazo que não existe.
  const tarefas = useMemo(() => atividades.filter(a => a.nivelHierarquico === 'TAREFA' && a.dataPlanejada && a.dataPrevista), [atividades]);

  const diasUteis = useMemo(
    () => listarDiasUteisNoIntervalo(cicloAtivo.dataAbertura, cicloAtivo.previsaoConclusao, feriados),
    [cicloAtivo, feriados]
  );

  const legenda = useMemo(() => {
    const status = Array.from(new Set(tarefas.map(a => a.status))) as StatusAtividade[];
    return status.sort();
  }, [tarefas]);

  if (tarefas.length === 0 || diasUteis.length === 0) return <EstadoVazio titulo='Sem dados suficientes para montar o Gantt' />;

  const indiceColuna = (isoCompleto: string): number => {
    // dataPlanejada/dataPrevista da atividade vêm como ISO completo ("2026-09-16T00:00:00"), mas
    // diasUteis guarda só "YYYY-MM-DD" — sem cortar a hora aqui, o indexOf nunca casa com nada.
    const iso = isoCompleto.split('T')[0];
    const indice = diasUteis.indexOf(iso);
    if (indice >= 0) return indice;
    // fora do intervalo padrão do ciclo — ancora na ponta mais próxima em vez de sumir da grade.
    return iso < diasUteis[0] ? 0 : diasUteis.length - 1;
  };

  const bloqueioDaAtividade = (a: Atividade): { nome: string; origemId: number } | undefined => {
    if (a.dependenciaIds.length === 0) return undefined;
    const dep = dependencias.find(d => a.dependenciaIds.includes(d.id));
    if (!dep) return undefined;
    const origem = atividades.find(t => t.id === dep.atividadeOrigemId);
    return origem ? { nome: origem.nome, origemId: origem.id } : undefined;
  };

  const motorDaAtividade = (a: Atividade): MotorConciliacaoDados | undefined => {
    if (a.produtoIntegradoTipo !== 'MOTOR_CONCILIACAO') return undefined;
    const produto = produtosIntegrados.find(p => p.tipo === 'MOTOR_CONCILIACAO' && p.empresaId === a.empresaId);
    return produto && isMotorConciliacao(produto) && produto.contasComDivergencia > 0 ? produto : undefined;
  };

  const colunas = `260px repeat(${diasUteis.length}, minmax(30px, 1fr))`;

  return (
    <Card title='Gantt' subtitle='Barras posicionadas por dias úteis do ciclo — role horizontalmente para ver o período completo.'>
      <div className='mc-gantt__legenda'>
        {legenda.map(status => (
          <span key={status}><i className={`mc-gantt__legenda-cor mc-gantt__legenda-cor--${COR_STATUS_ATIVIDADE[status]}`} />{LABEL_STATUS_ATIVIDADE[status]}</span>
        ))}
        <span><i className='bi bi-link-45deg' /> Botão de link (ao lado do nome): vai direto à atividade que está bloqueando</span>
        <span><i className='bi bi-exclamation-triangle-fill' /> Botão de alerta (ao lado do nome): mostra as contas com divergência do Motor de Conciliação</span>
      </div>

      <div className='mc-gantt__scroll'>
        <div className='mc-gantt__linha mc-gantt__linha--cabecalho' style={{ gridTemplateColumns: colunas }}>
          <div className='mc-gantt__canto' />
          {diasUteis.map(dia => (
            <div className='mc-gantt__cabecalho' key={dia}>{dia.slice(8, 10)}/{dia.slice(5, 7)}</div>
          ))}
        </div>
        {tarefas.map(a => {
          const colunaInicio = indiceColuna(a.dataPlanejada) + 2;
          const colunaFim = indiceColuna(a.dataPrevista) + 3;
          const bloqueio = bloqueioDaAtividade(a);
          const motor = motorDaAtividade(a);
          return (
            <div className='mc-gantt__linha' style={{ gridTemplateColumns: colunas }} key={a.id}>
              <div className='mc-gantt__linha-titulo' title={a.nome}>
                <button type='button' className='mc-gantt__nome' onClick={() => onSelecionar(a.id)}>
                  {a.caminhoCritico && <i className='bi bi-lightning-charge-fill mc-icone-critico' />} {a.nome}
                </button>
                <span className='mc-gantt__acoes'>
                  {bloqueio && (
                    <button
                      type='button'
                      className='mc-gantt__drill'
                      title={`Ver atividade que está bloqueando: ${bloqueio.nome}`}
                      onClick={() => onSelecionar(bloqueio.origemId)}
                    >
                      <i className='bi bi-link-45deg' />
                    </button>
                  )}
                  {motor && (
                    <button
                      type='button'
                      className='mc-gantt__drill mc-gantt__drill--alerta'
                      title={`Ver as ${motor.contasComDivergencia} contas com divergência da conciliação`}
                      onClick={() => onSelecionar(a.id, 'produto')}
                    >
                      <i className='bi bi-exclamation-triangle-fill' /> {motor.contasComDivergencia}
                    </button>
                  )}
                </span>
              </div>
              <div
                className={`mc-gantt__barra mc-gantt__barra--${COR_STATUS_ATIVIDADE[a.status]}`}
                style={{ gridColumn: `${colunaInicio} / ${colunaFim}` }}
                title={`${formatarData(a.dataPlanejada)} — ${formatarData(a.dataPrevista)}`}
                onClick={() => onSelecionar(a.id)}
              />
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default VisaoGantt;
